<template>
  <div id="map-wrap">
    <MglMap
      :access-token="accessToken"
      :map-style="mapStyle"
      :center="center"
      :zoom.sync="zoom"
      :keyboard="false"
      @load="onMapLoaded"
      @dragend="filterPointList()"
      @click="clickHandler"
    >
      <MglNavigationControl ref="controls" position="top-right" />
      <MarkerWrapper
        v-if="pointListInFrame.length < 100"
        :point-list="pointListInFrame"
        :has-edit-permission="hasEditPermission"
      />
    </MglMap>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MglMap, MglNavigationControl } from 'vue-mapbox'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import MarkerWrapper from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Map/mapbox/MarkerWrapper'
import RouteHelper from '@/mixins/RouteHelper'
import { minBy, maxBy } from 'lodash/math'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'
import debounce from 'lodash/debounce'

export default {
  name: 'App',
  components: {
    MarkerWrapper,
    MglMap,
    MglNavigationControl,
  },
  mixins: [RouteHelper],
  props: {
    pointList: {
      type: Array,
      default: () => [],
    },
    hasEditPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accessToken:
        'pk.eyJ1Ijoid25vZ3VjaGkiLCJhIjoiY2lqY2c2dmxuMDAxZHUxamJwMGl3ZnV5ciJ9.WBilN5epB7RdyO-JbUQd2A',
      mapStyle: 'mapbox://styles/wnoguchi/ckbu3bvc303t41ip4yzmywi29',
      zoom: 17,
      timer: null, // ダブルクリックとクリックを見分けるためのタイマー
      pointListInFrame: [],
    }
  },
  computed: {
    ...mapGetters('pointGroupOperation', [
      'mapCenter',
      'targetGroup',
      'selectedPoint',
      'fitBounds',
      'lastDragEndTime',
      'pagePermission',
      'isEditingPoiCategory',
    ]),
    center() {
      const selectedPoint = this.mapCenter
      return [selectedPoint.longitude, selectedPoint.latitude]
    },
    canAddPoint() {
      return !this.underSystemOwnerPage
    },
  },
  watch: {
    pointList() {
      this.filterPointList()
    },
    zoom() {
      this.filterPointList()
    },
    fitBounds(triggerFlag) {
      if (!triggerFlag) return
      if (this.pointList.length === 0) return
      if (this.pointList.length === 1) {
        // 地点が1つだけの場合は選択する
        this.$nextTick(async () => {
          this.setSelectedPoint({ data: this.pointList[0] })
        })
        return
      }

      this.$nextTick(() => {
        const lngLats = this.pointList.map(point => ({
          lng: point.shape.longitude,
          lat: point.shape.latitude,
        }))
        const maxLng = maxBy(lngLats, lngLat => lngLat.lng)
        const minLng = minBy(lngLats, lngLat => lngLat.lng)
        const maxLat = maxBy(lngLats, lngLat => lngLat.lat)
        const minLat = minBy(lngLats, lngLat => lngLat.lat)

        const padding = 50

        // 南西と東北の座標を指定して全ての地点が入る矩形を指定する
        this.map.fitBounds(
          [
            [minLng.lng, minLat.lat],
            [maxLng.lng, maxLat.lat],
          ],
          { padding },
        )
      })
    },
    selectedPoint(newPoint) {
      if (!newPoint || !('shape' in newPoint)) return
      this.$nextTick(() => {
        this.flyTo(newPoint)
      })
    },
  },
  created() {
    this.map = null
  },
  beforeDestroy() {
    // ページ遷移するタイミングで MglNavigationControl が残っているとエラーが発生するので強制削除
    this.$refs.controls.$destroy()
  },
  methods: {
    ...mapActions('pointGroupOperation', {
      addNewPointByLatLng: 'ADD_NEW_POINT_BY_LAT_LNG',
      setSelectedPoint: 'SET_SELECTED_POINT',
      setMapLoaded: 'SET_MAP_LOADED',
      setPointListInFrameCount: 'SET_POINT_LIST_IN_FRAME_COUNT',
    }),
    onMapLoaded(event) {
      this.map = event.map
      this.$store.map = event.map

      this.setMapLoaded()

      // ここで地図を日本語化
      // ※ vue-mapbox が load 後の map オブジェクトしか提供していないので一瞬英語の地図が表示されてしまう
      const language = new MapboxLanguage()
      this.map.setStyle(language.setLanguage(this.map.getStyle(), 'ja'))
    },
    async clickHandler(e) {
      // カテゴリーグループの場合は、クリックによる地点追加はできない
      if (this.targetGroup.locationType === locationGroupType.POI_LOCATION)
        return
      if (this.isEditingPoiCategory) return

      /**
       * 最終ドラッグから0.5秒以内のクリックイベントは無視する（人間の反射神経は0.5秒が限界だとバキで読んだ）
       * @see https://github.com/geo-logic/geologic-admin-scrum/issues/245
       */
      const lastDragEndTime = this.lastDragEndTime
      if (new Date().getTime() - lastDragEndTime <= 500) return

      const pagePermission = this.pagePermission
      if (pagePermission !== 'edit' || !this.canAddPoint) {
        return
      }

      /**
       * ダブルクリックした時は地点を作成しないようにする
       */
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
        return
      }

      this.timer = setTimeout(() => {
        this.timer = null
        if (this.targetGroup.id) {
          this.addNewPointByLatLng(e.mapboxEvent.lngLat)
        } else {
          alert('地点グループを選択してください')
        }
      }, 300)
    },
    flyTo(point) {
      return new Promise(resolve => {
        if (this.map === undefined) return resolve()
        const { longitude, latitude } = point.shape
        this.map.flyTo({ center: [longitude, latitude], zoom: 17 })
        this.map.once('moveend', () => {
          this.filterPointList()
          resolve()
        })
      })
    },
    resize() {
      if (!this.map) {
        return
      }
      this.$nextTick(() => {
        this.map.resize()
      })
    },
    filterPointList: debounce(function() {
      if (!this.map) return
      const frame = this.map.getBounds()
      this.pointListInFrame = this.pointList.filter(point => {
        // 表示域内の地点をフィルター
        return (
          (point.shape.longitude - frame._ne.lng) *
            (point.shape.longitude - frame._sw.lng) <=
            0 &&
          (point.shape.latitude - frame._ne.lat) *
            (point.shape.latitude - frame._sw.lat) <=
            0
        )
      })
      this.setPointListInFrameCount(this.pointListInFrame.length)
    }),
  },
}
</script>

<style>
#map-wrap {
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
}
</style>
